/**
 * SummaryRibbon Component
 *
 * Displays a summary ribbon at the top of the event list/timeline
 * showing key statistics about recent activity.
 */

import { useMemo } from 'react';
import { UnifiedEvent, EventType, EVENT_TYPE_INFO } from '../types/events';

interface SummaryRibbonProps {
  events: UnifiedEvent[];
  locationName: string;
}

/**
 * Priority order for summary display.
 */
const SUMMARY_PRIORITY: EventType[] = [
  'FIRE_INCIDENT',
  'AIRCRAFT',
  'HOTSPOT',
  'WEATHER',
  'TRANSPORT',
  'POWER_OUTAGE',
  'COMMUNITY_REPORT',
];

export default function SummaryRibbon({
  events,
  locationName,
}: SummaryRibbonProps) {
  const summary = useMemo(() => {
    // Count events by type
    const countByType: Partial<Record<EventType, number>> = {};

    for (const event of events) {
      countByType[event.type] = (countByType[event.type] || 0) + 1;
    }

    // Build summary parts in priority order
    const parts: string[] = [];

    for (const type of SUMMARY_PRIORITY) {
      const count = countByType[type];
      if (count && count > 0) {
        const info = EVENT_TYPE_INFO[type];
        const label = count === 1 ? info.label.toLowerCase() : `${info.label.toLowerCase()}s`;
        parts.push(`${count} ${label}`);
      }
    }

    return parts;
  }, [events]);

  if (events.length === 0) {
    return (
      <div className="summary-ribbon summary-ribbon-clear">
        <span className="summary-icon">✓</span>
        <span className="summary-text">
          No active fire-related incidents near {locationName}.
        </span>
      </div>
    );
  }

  // Check if there are any emergency-level fires
  const hasEmergency = events.some(
    (e) => e.type === 'FIRE_INCIDENT' && (e.data as any)?.severity === 'EMERGENCY'
  );

  return (
    <div
      className={`summary-ribbon ${hasEmergency ? 'summary-ribbon-emergency' : 'summary-ribbon-active'}`}
    >
      <span className="summary-icon">{hasEmergency ? '⚠️' : '🔥'}</span>
      <span className="summary-text">
        Active incidents near {locationName}: {summary.join(', ')}.
      </span>
    </div>
  );
}
